// Blocks -> email HTML + plain text. Used by the editor preview and by the sender, so they match.
// Email HTML rules: tables for layout, inline styles, 600px wide, images with alt text.

import type { Block, Img } from './blocks';
import type { LookId, Settings } from './types';
import { getLook, type Look } from './looks';
import { escAttr, escHtml, richToText, safeUrl, sanitizeRich, decodeEntities } from './sanitize';

export type FooterSettings = Pick<Settings, 'address' | 'phone' | 'website' | 'from_name' | 'instagram'>;

export interface RenderInput {
  blocks: Block[];
  look: LookId;
  subject: string;
  preheader: string;
}

export interface RenderOpts {
  baseUrl: string; // where /stock, /brand and /img are served
  settings: FooterSettings;
  mode: 'send' | 'preview';
  editable?: boolean; // adds data-* hooks for the editor
  dateLabel?: string; // e.g. "November 2026"
}

export interface Rendered {
  html: string;
  text: string;
  links: string[]; // send mode: {{LINK_n}} placeholders point into this list
}

/** Width of the email column. */
export const WIDTH = 600;

/** Shape each image slot expects (width / height). The editor crops uploads to this. */
export function slotAspect(type: Block['type']): number | null {
  switch (type) {
    case 'hero':
      return 3 / 2;
    case 'split':
    case 'tiles':
      return 4 / 5;
    case 'quote':
      return 1;
    default:
      return null; // free shape
  }
}

interface Ctx {
  L: Look;
  o: RenderOpts;
  link: (url: string) => string;
  img: (src: string) => string;
  ed: (id: string, path: string, kind?: 'plain' | 'rich') => string;
  edImg: (id: string, path: string) => string;
  text: string[];
}

export function render(input: RenderInput, opts: RenderOpts): Rendered {
  const L = getLook(input.look);
  const links: string[] = [];
  const base = opts.baseUrl.replace(/\/+$/, '');
  const ctx: Ctx = {
    L,
    o: opts,
    text: [],
    link(url) {
      const safe = safeUrl(url);
      if (!safe) return '#';
      if (opts.mode === 'preview') return escAttr(safe);
      let n = links.indexOf(safe);
      if (n === -1) n = links.push(safe) - 1;
      return `{{LINK_${n}}}`;
    },
    img(src) {
      return escAttr(/^https?:\/\//i.test(src) ? src : base + (src.startsWith('/') ? src : '/' + src));
    },
    ed(id, path, kind = 'plain') {
      return opts.editable ? ` data-edit="${escAttr(`${id}|${path}`)}" data-kind="${kind}"` : '';
    },
    edImg(id, path) {
      return opts.editable ? ` data-img="${escAttr(`${id}|${path}`)}"` : '';
    },
  };

  const rows = input.blocks.map((b) => {
    const inner = renderBlock(b, ctx);
    return opts.editable ? inner.replace(/^<tr/, () => `<tr data-block="${escAttr(b.id)}" data-type="${escAttr(b.type)}"`) : inner;
  });

  const body = [header(ctx), ...rows, footer(ctx)].join('\n');
  const pre = escHtml(input.preheader || '');
  const html = `<!doctype html>
<html lang="en" xmlns="http://www.w3.org/1999/xhtml">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<meta name="x-apple-disable-message-reformatting">
<meta name="color-scheme" content="light">
<meta name="supported-color-schemes" content="light">
<title>${escHtml(input.subject || '')}</title>
<link href="${escAttr(L.fontsHref)}" rel="stylesheet">
<style>
body{margin:0;padding:0;-webkit-text-size-adjust:100%;}
table{border-collapse:collapse;}
img{border:0;outline:none;text-decoration:none;-ms-interpolation-mode:bicubic;}
a{color:inherit;}
@media only screen and (max-width:620px){
.wrap{width:100%!important;}
.col{display:block!important;width:100%!important;max-width:100%!important;box-sizing:border-box!important;}
.px{padding-left:20px!important;padding-right:20px!important;}
.h1{font-size:${Math.round(L.heroH1 * 0.82)}px!important;}
.tall{height:440px!important;}
.full img{width:100%!important;}
}
</style>
</head>
<body style="margin:0;padding:0;background:${L.outer};">
<div style="display:none;max-height:0;overflow:hidden;mso-hide:all;font-size:1px;line-height:1px;color:${L.outer};">${pre}${'&#8204;&nbsp;'.repeat(60)}</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${L.outer};" bgcolor="${L.outer}">
<tr><td align="center" style="padding:24px 0;">
<table role="presentation" class="wrap" width="${WIDTH}" cellpadding="0" cellspacing="0" border="0" style="width:${WIDTH}px;max-width:${WIDTH}px;background:${L.paper};" bgcolor="${L.paper}">
${body}
</table>
</td></tr>
</table>
${opts.mode === 'send' ? '{{OPEN_PIXEL}}' : ''}
</body>
</html>`;

  const text = buildText(input, ctx);
  return { html, text, links };
}

// ---------- text helpers ----------

function font(L: Look, kind: 'head' | 'body'): string {
  return kind === 'head' ? L.headFont : L.bodyFont;
}

function hStyle(L: Look, size: number, color = L.heading, extra = ''): string {
  return `margin:0;font-family:${font(L, 'head')};font-weight:${L.headWeight};font-size:${size}px;line-height:1.12;letter-spacing:${L.headTracking};color:${color};${extra}`;
}

function pStyle(L: Look, size = 16, color = L.text, extra = ''): string {
  return `margin:0;font-family:${font(L, 'body')};font-size:${size}px;line-height:1.6;color:${color};${extra}`;
}

function kickerStyle(L: Look, color = L.accent): string {
  return `margin:0 0 8px;font-family:${font(L, 'body')};font-size:12px;font-weight:600;letter-spacing:0.08em;text-transform:uppercase;color:${color};`;
}

/** Rich text: sanitize, route links through tracking, style the links. */
function rich(html: string, c: Ctx, linkColor: string): string {
  return sanitizeRich(html).replace(/<a href="([^"]*)">/g, (_m, href: string) => {
    return `<a href="${c.link(decodeEntities(href))}" style="color:${linkColor};font-weight:600;text-decoration:underline;">`;
  });
}

function plain(s: string): string {
  return escHtml(s || '');
}

function imgTag(c: Ctx, image: Img, width: number, opts: { radius?: number; edit?: string; extra?: string } = {}): string {
  const h = Math.round((width * image.height) / Math.max(1, image.width));
  const r = opts.radius ?? 0;
  return `<img src="${c.img(image.src)}" alt="${escAttr(image.alt || '')}" width="${width}" height="${h}"${opts.edit ?? ''} style="display:block;width:100%;max-width:${width}px;height:auto;border:0;${r ? `border-radius:${r}px;` : ''}${opts.extra ?? ''}">`;
}

function button(c: Ctx, label: string, url: string, dark = false, full = c.L.buttonFull, edit = ''): string {
  const L = c.L;
  const bg = dark ? L.darkButtonBg : L.buttonBg;
  const fg = dark ? L.darkButtonFg : L.buttonFg;
  const r = L.buttonRadius;
  const a = `<a href="${c.link(url)}"${edit} style="display:${full ? 'block' : 'inline-block'};padding:15px 30px;font-family:${font(L, 'body')};font-size:16px;font-weight:600;line-height:1.2;color:${fg};text-decoration:none;border-radius:${r}px;text-align:center;">${plain(label)}</a>`;
  return `<table role="presentation" ${full ? 'width="100%"' : 'align="center"'} cellpadding="0" cellspacing="0" border="0" style="margin:0 auto;"><tr><td bgcolor="${bg}" style="background:${bg};border-radius:${r}px;text-align:center;">${a}</td></tr></table>`;
}

function row(c: Ctx, inner: string, padTop = 28, padBottom = 28, extra = ''): string {
  const p = c.L.pad;
  return `<tr><td class="px" style="padding:${padTop}px ${p}px ${padBottom}px;${extra}">${inner}</td></tr>`;
}

// ---------- header & footer ----------

function header(c: Ctx): string {
  const { L, o } = c;
  const logo = (h: number) => {
    const src = L.logo === 'white' ? '/brand/logo-white.png' : '/brand/logo.png';
    const w = Math.round((h * 240) / 225);
    return `<img src="${c.img(src)}" alt="${escAttr(o.settings.from_name || 'LaVir Med Spa')}" width="${w}" height="${h}" style="display:block;width:${w}px;height:${h}px;border:0;">`;
  };
  const date = o.dateLabel ? plain(o.dateLabel) : '';
  if (L.header === 'left') {
    return `<tr><td class="px" style="padding:20px ${L.pad}px;border-bottom:1px solid ${L.line};"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td>${logo(46)}</td><td align="right" style="font-family:${L.bodyFont};font-size:12px;letter-spacing:0.04em;color:${L.muted};">${date}</td></tr></table></td></tr>`;
  }
  if (L.header === 'masthead') {
    return `<tr><td class="px" align="center" style="padding:28px ${L.pad}px 16px;border-bottom:1px solid ${L.heading};"><div style="${hStyle(L, 50, L.heading, 'line-height:1;')}">LaVir Notes</div></td></tr>
<tr><td class="px" style="padding:9px ${L.pad}px;border-bottom:1px solid ${L.line};"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="font-family:${L.bodyFont};font-size:12px;color:${L.muted};">${date}</td><td align="right" style="font-family:${L.bodyFont};font-size:12px;color:${L.muted};">Antioch, Illinois</td></tr></table></td></tr>`;
  }
  return `<tr><td align="center" style="padding:24px ${L.pad}px 18px;"><table role="presentation" align="center" cellpadding="0" cellspacing="0" border="0"><tr><td>${logo(L.id === 'onething' ? 40 : 56)}</td></tr></table></td></tr>`;
}

function footer(c: Ctx): string {
  const { L, o } = c;
  const s = o.settings;
  const unsub = o.mode === 'send' ? '{{UNSUB_URL}}' : '#';
  const site = s.website ? `<a href="${c.link(s.website)}" style="color:${L.footerFg};">${plain(s.website.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, ''))}</a>` : '';
  const ig = s.instagram ? ` &middot; <a href="${c.link(s.instagram)}" style="color:${L.footerFg};">Instagram</a>` : '';
  const line2 = [s.phone ? plain(s.phone) : '', site].filter(Boolean).join(' &middot; ');
  return `<tr><td class="px" align="center" bgcolor="${L.footerBg}" style="padding:26px ${L.pad}px 34px;background:${L.footerBg};border-top:1px solid ${L.footerBg === L.paper ? L.line : L.footerBg};font-family:${L.bodyFont};font-size:12px;line-height:1.7;color:${L.footerFg};text-align:center;" data-locked="footer">
${plain(s.from_name || 'LaVir Med Spa')} &middot; ${plain(s.address)}<br>${line2}${ig}<br><br>
You are getting this email because you are a client of ${plain(s.from_name || 'LaVir Med Spa')}.<br>
<a href="${unsub}" style="color:${L.footerFg};text-decoration:underline;">Unsubscribe</a>
</td></tr>`;
}

// ---------- blocks ----------

function renderBlock(b: Block, c: Ctx): string {
  const { L } = c;
  const P = L.pad;
  const inner = WIDTH - 2 * P;
  switch (b.type) {
    case 'hero': {
      c.text.push(b.headline, b.text);
      if (b.overlay) {
        const src = c.img(b.image.src);
        const pic = `<tr><td class="tall" height="460" valign="bottom" background="${src}" bgcolor="#2b2f33" style="height:460px;vertical-align:bottom;background:#2b2f33 url('${src}') center 30% / cover no-repeat;background-size:cover;"${c.edImg(b.id, 'image')}>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td class="px" style="padding:0 ${P}px 28px;">
<h1 class="h1"${c.ed(b.id, 'headline')} style="${hStyle(L, L.heroH1 + 8, '#ffffff', 'line-height:1.0;text-shadow:0 2px 24px rgba(0,0,0,0.35);')}">${plain(b.headline)}</h1>
</td></tr></table></td></tr>`;
        const words = b.text || c.o.editable ? row(c, `<p${c.ed(b.id, 'text')} style="${pStyle(L, 18, L.text)}">${plain(b.text)}</p>`, 26, 6) : '';
        return `<tr><td><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">${pic}${words}</table></td></tr>`;
      }
      const full = L.radius === 0;
      const pic = full
        ? `<tr><td class="full">${imgTag(c, b.image, WIDTH, { edit: c.edImg(b.id, 'image') })}</td></tr>`
        : `<tr><td class="px" style="padding:0 ${P}px;">${imgTag(c, b.image, inner, { radius: L.radius, edit: c.edImg(b.id, 'image') })}</td></tr>`;
      const words = `<tr><td class="px" style="padding:34px ${P}px 26px;">
<h1 class="h1"${c.ed(b.id, 'headline')} style="${hStyle(L, L.heroH1)}">${plain(b.headline)}</h1>
${b.text || c.o.editable ? `<p${c.ed(b.id, 'text')} style="${pStyle(L, 16, L.muted, 'margin-top:14px;')}">${plain(b.text)}</p>` : ''}
</td></tr>`;
      // Wrap in one row so the editor treats the hero as one block.
      return `<tr><td><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">${words}${pic}</table></td></tr>`;
    }

    case 'heading': {
      c.text.push((b.kicker ? b.kicker.toUpperCase() + '\n' : '') + b.text);
      const size = b.size === 'l' ? Math.round(L.heroH1 * 0.9) : L.h2;
      return row(
        c,
        `${b.kicker || c.o.editable ? `<p${c.ed(b.id, 'kicker')} style="${kickerStyle(L)}">${plain(b.kicker)}</p>` : ''}<h2${c.ed(b.id, 'text')} style="${hStyle(L, size)}">${plain(b.text)}</h2>`,
        30,
        4,
      );
    }

    case 'text':
      c.text.push(richToText(sanitizeRich(b.html)));
      return row(c, `<div${c.ed(b.id, 'html', 'rich')} style="${pStyle(L, 16)}">${rich(b.html, c, L.heading)}</div>`, 14, 14);

    case 'image': {
      c.text.push(b.caption);
      const pic = imgTag(c, b.image, L.radius === 0 ? WIDTH : inner, { radius: L.radius, edit: c.edImg(b.id, 'image') });
      const linked = b.link && !c.o.editable ? `<a href="${c.link(b.link)}" style="display:block;">${pic}</a>` : pic;
      const cap = b.caption || c.o.editable ? `<p${c.ed(b.id, 'caption')} style="${pStyle(L, 13, L.muted, 'margin-top:8px;')}">${plain(b.caption)}</p>` : '';
      if (L.radius === 0) {
        return `<tr><td class="full" style="padding:12px 0;">${linked}${cap ? `<div class="px" style="padding:0 ${P}px;">${cap}</div>` : ''}</td></tr>`;
      }
      return row(c, linked + cap, 12, 12);
    }

    case 'button':
      c.text.push(`${b.label}: ${b.url}`);
      return row(
        c,
        `${button(c, b.label, b.url, false, L.buttonFull, c.ed(b.id, 'label'))}${b.note || c.o.editable ? `<p${c.ed(b.id, 'note')} style="${pStyle(L, 13, L.muted, 'margin-top:10px;text-align:center;')}">${plain(b.note)}</p>` : ''}`,
        18,
        26,
      );

    case 'split': {
      c.text.push([b.kicker.toUpperCase(), b.heading, richToText(sanitizeRich(b.html)), b.linkLabel ? `${b.linkLabel}: ${b.url}` : ''].filter(Boolean).join('\n'));
      const imgCell = `<td class="col" width="300" valign="top" style="width:300px;">${imgTag(c, b.image, 300, { edit: c.edImg(b.id, 'image') })}</td>`;
      const textCell = `<td class="col px" width="300" valign="middle" style="width:300px;padding:28px;">
${b.kicker || c.o.editable ? `<p${c.ed(b.id, 'kicker')} style="${kickerStyle(L)}">${plain(b.kicker)}</p>` : ''}
<h3${c.ed(b.id, 'heading')} style="${hStyle(L, 22, L.heading, 'margin-bottom:10px;line-height:1.2;')}">${plain(b.heading)}</h3>
<div${c.ed(b.id, 'html', 'rich')} style="${pStyle(L, 14, L.muted, 'margin-bottom:12px;')}">${rich(b.html, c, L.heading)}</div>
${b.linkLabel ? `<a href="${c.link(b.url)}"${c.ed(b.id, 'linkLabel')} style="font-family:${L.bodyFont};font-size:14px;font-weight:600;color:${L.heading};text-decoration:underline;">${plain(b.linkLabel)}</a>` : ''}
</td>`;
      const cells = b.imageSide === 'right' ? textCell + imgCell : imgCell + textCell;
      return `<tr><td style="padding:12px 0;border-top:1px solid ${L.line};border-bottom:1px solid ${L.line};"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>${cells}</tr></table></td></tr>`;
    }

    case 'tiles': {
      const w = Math.floor((WIDTH - 4) / 2);
      const cells = b.items
        .slice(0, 2)
        .map((t, i) => {
          c.text.push(`${t.label}: ${t.url}`);
          const pic = imgTag(c, t.image, w, { edit: c.edImg(b.id, `items.${i}.image`) });
          const label = `<p${c.ed(b.id, `items.${i}.label`)} style="${pStyle(L, 15, L.heading, 'font-weight:600;margin:10px 0 0;')}">${plain(t.label)}</p>`;
          const body = c.o.editable ? pic + label : `<a href="${c.link(t.url)}" style="text-decoration:none;color:${L.heading};">${pic}${label}</a>`;
          return `<td class="col" width="${w}" valign="top" style="width:${w}px;padding:0 0 16px;">${body}</td>`;
        });
      return `<tr><td style="padding:16px 0 8px;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>${cells[0] ?? ''}<td width="4" style="width:4px;font-size:0;">&nbsp;</td>${cells[1] ?? ''}</tr></table></td></tr>`;
    }

    case 'timeline':
      return timeline(b, c);

    case 'prices':
      return prices(b, c);

    case 'offer': {
      c.text.push(`${b.heading}\n${b.text}\n${b.label}: ${b.url}`);
      if (b.style === 'dark') {
        return `<tr><td class="px" align="center" bgcolor="${L.darkBg}" style="padding:34px ${P}px;background:${L.darkBg};text-align:center;">
<h3${c.ed(b.id, 'heading')} style="${hStyle(L, 26, L.darkFg, 'margin-bottom:8px;')}">${plain(b.heading)}</h3>
<p${c.ed(b.id, 'text')} style="${pStyle(L, 15, L.darkMuted, 'margin-bottom:20px;')}">${plain(b.text)}</p>
${button(c, b.label, b.url, true, false, c.ed(b.id, 'label'))}
</td></tr>`;
      }
      return row(
        c,
        `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td align="center" bgcolor="${L.cardBg}" style="padding:30px 24px;background:${L.cardBg};border-radius:${Math.min(L.radius, 12)}px;text-align:center;">
<h3${c.ed(b.id, 'heading')} style="${hStyle(L, 24, L.heading, 'margin-bottom:8px;')}">${plain(b.heading)}</h3>
<p${c.ed(b.id, 'text')} style="${pStyle(L, 15, L.muted, 'margin-bottom:20px;')}">${plain(b.text)}</p>
${button(c, b.label, b.url, false, false, c.ed(b.id, 'label'))}
</td></tr></table>`,
        18,
        18,
      );
    }

    case 'quote': {
      c.text.push(`${b.question}\n${b.answer}\n${b.name}`);
      const words = `<p${c.ed(b.id, 'question')} style="${hStyle(L, 21, L.heading, 'margin-bottom:8px;line-height:1.25;')}">${plain(b.question)}</p>
<p${c.ed(b.id, 'answer')} style="${pStyle(L, 15, L.muted, 'margin-bottom:8px;')}">${plain(b.answer)}</p>
<p${c.ed(b.id, 'name')} style="${pStyle(L, 13, L.accent, 'font-weight:600;')}">${plain(b.name)}</p>`;
      if (!b.photo) {
        return row(c, `<div style="border-top:1px solid ${L.heading};border-bottom:1px solid ${L.heading};padding:24px 0;">${words}</div>`, 24, 24);
      }
      const photo = `<img src="${c.img(b.photo.src)}" alt="${escAttr(b.photo.alt)}" width="72" height="72"${c.edImg(b.id, 'photo')} style="display:block;width:72px;height:72px;border-radius:36px;border:0;">`;
      return row(
        c,
        `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td width="72" valign="top" style="width:72px;padding-right:18px;">${photo}</td><td valign="top">${words}</td></tr></table>`,
        26,
        26,
        `border-top:1px solid ${L.line};border-bottom:1px solid ${L.line};`,
      );
    }

    case 'divider':
      return `<tr><td class="px" style="padding:8px ${P}px;"><div style="border-top:1px solid ${L.line};height:1px;line-height:1px;font-size:0;">&nbsp;</div></td></tr>`;

    case 'spacer': {
      const h = b.size === 's' ? 12 : b.size === 'l' ? 48 : 28;
      return `<tr><td height="${h}" style="height:${h}px;line-height:${h}px;font-size:0;">&nbsp;</td></tr>`;
    }
  }
}

function timeline(b: Extract<Block, { type: 'timeline' }>, c: Ctx): string {
  const { L } = c;
  c.text.push(b.title.toUpperCase() + '\n' + b.rows.map((r) => `${r.label}: ${r.heading}. ${r.text}`).join('\n'));
  const title = !b.title && !c.o.editable
    ? ''
    : L.timeline === 'rail'
      ? `<p${c.ed(b.id, 'title')} style="${kickerStyle(L)}margin-bottom:18px;">${plain(b.title)}</p>`
      : `<h3${c.ed(b.id, 'title')} style="${hStyle(L, L.h2, L.heading, `margin-bottom:16px;${L.timeline === 'list' ? 'text-align:center;' : ''}`)}">${plain(b.title)}</h3>`;
  const rows = b.rows
    .map((r, i) => {
      const last = i === b.rows.length - 1;
      const lab = `<span${c.ed(b.id, `rows.${i}.label`)}>${plain(r.label)}</span>`;
      const head = `<span${c.ed(b.id, `rows.${i}.heading`)}>${plain(r.heading)}</span>`;
      const txt = r.text || c.o.editable ? `<span${c.ed(b.id, `rows.${i}.text`)}>${plain(r.text)}</span>` : '';
      if (L.timeline === 'rail') {
        const dot = `<span style="display:inline-block;width:10px;height:10px;border-radius:7px;border:2px solid ${last ? L.accent : L.buttonBg};background:${last ? L.accent : L.paper};margin:0 0 0 -27px;vertical-align:top;"></span>`;
        return `<tr><td width="96" valign="top" style="width:96px;padding:2px 14px 22px 0;text-align:right;font-family:${L.bodyFont};font-size:13px;font-weight:600;color:${L.accent};">${lab}</td>
<td valign="top" style="padding:0 0 22px 20px;border-left:2px solid ${L.line};">${dot}<div style="margin-top:-12px;font-family:${L.bodyFont};font-size:16px;font-weight:600;color:${L.heading};line-height:1.4;">${head}</div><div style="${pStyle(L, 14, L.muted, 'line-height:1.5;')}">${txt}</div></td></tr>`;
      }
      if (L.timeline === 'table') {
        return `<tr><td width="110" valign="top" style="width:110px;padding:12px 0;border-top:1px solid ${L.line};font-family:${L.bodyFont};font-size:15px;font-weight:600;color:${L.accent};">${lab}</td>
<td valign="top" style="padding:12px 0;border-top:1px solid ${L.line};font-family:${L.bodyFont};font-size:15px;line-height:1.45;color:${L.text};"><b style="color:${L.heading};">${head}.</b> ${txt}</td></tr>`;
      }
      return `<tr><td valign="top" style="padding:12px 0;border-top:1px solid ${L.line};font-family:${L.bodyFont};font-size:15px;line-height:1.45;"><div style="color:${L.heading};font-weight:600;">${head}</div>${
        txt ? `<div style="color:${L.muted};font-size:14px;">${txt}</div>` : ''
      }</td><td valign="top" align="right" style="padding:12px 0 12px 16px;border-top:1px solid ${L.line};font-family:${L.bodyFont};font-size:15px;color:${L.accent};white-space:nowrap;">${lab}</td></tr>`;
    })
    .join('\n');
  return row(c, `${title}<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">${rows}</table>`, 30, 18);
}

function prices(b: Extract<Block, { type: 'prices' }>, c: Ctx): string {
  const { L } = c;
  c.text.push(
    b.title.toUpperCase() +
      '\n' +
      b.groups.map((g) => g.name + '\n' + g.items.map((it) => `  ${it.name}: ${it.price}${it.unit}${it.was ? ` (was ${it.was})` : ''}`).join('\n')).join('\n') +
      (b.note ? '\n' + b.note : ''),
  );
  const groups = b.groups
    .map((g, gi) => {
      const items = g.items
        .map((it, ii) => {
          const p = `groups.${gi}.items.${ii}`;
          const was = it.was || c.o.editable ? `<s${c.ed(b.id, `${p}.was`)} style="font-family:${L.bodyFont};font-size:14px;color:${L.muted};margin-right:6px;">${plain(it.was)}</s>` : '';
          const unit = it.unit || c.o.editable ? `<span${c.ed(b.id, `${p}.unit`)} style="font-family:${L.bodyFont};font-size:13px;color:${L.muted};"> ${plain(it.unit)}</span>` : '';
          return `<tr><td valign="bottom" style="padding:9px 0;border-bottom:1px dotted ${L.line};font-family:${L.bodyFont};font-size:16px;font-weight:600;color:${L.heading};"><span${c.ed(b.id, `${p}.name`)}>${plain(it.name)}</span></td>
<td valign="bottom" align="right" style="padding:9px 0 9px 12px;border-bottom:1px dotted ${L.line};white-space:nowrap;">${was}<span${c.ed(b.id, `${p}.price`)} style="font-family:${L.headFont};font-size:24px;line-height:1;color:${L.heading};">${plain(it.price)}</span>${unit}</td></tr>`;
        })
        .join('\n');
      return `<tr><td colspan="2" style="padding:${gi === 0 ? 0 : 18}px 0 4px;"><p${c.ed(b.id, `groups.${gi}.name`)} style="${kickerStyle(L)}margin:0;">${plain(g.name)}</p></td></tr>${items}`;
    })
    .join('\n');
  const note = b.note || c.o.editable ? `<p${c.ed(b.id, 'note')} style="${pStyle(L, 13, L.muted, 'margin-top:12px;line-height:1.45;')}">${plain(b.note)}</p>` : '';
  return row(
    c,
    `${b.title || c.o.editable ? `<h3${c.ed(b.id, 'title')} style="${hStyle(L, L.h2, L.heading, 'margin-bottom:14px;text-align:center;')}">${plain(b.title)}</h3>` : ''}
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td bgcolor="${L.cardBg}" style="padding:22px 22px 16px;background:${L.cardBg};border-radius:${Math.min(L.radius, 8)}px;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">${groups}</table>${note}
</td></tr></table>`,
    26,
    18,
  );
}

// ---------- plain-text version ----------

function buildText(input: RenderInput, c: Ctx): string {
  const s = c.o.settings;
  const parts = [input.preheader, ...c.text].map((t) => (t || '').trim()).filter(Boolean);
  const unsub = c.o.mode === 'send' ? '{{UNSUB_URL}}' : '';
  return [
    ...parts,
    '---',
    `${s.from_name} · ${s.address}`,
    [s.phone, s.website].filter(Boolean).join(' · '),
    `You are getting this email because you are a client of ${s.from_name}.`,
    unsub ? `Unsubscribe: ${unsub}` : '',
  ]
    .filter((x) => x !== '')
    .join('\n\n');
}

// ---------- per-person fill-in ----------

export interface PersonVars {
  firstName: string | null;
  unsubUrl: string;
  openPixelUrl: string | null;
  linkUrl: (n: number) => string;
}

const FIRST_NAME_RE = /\{\{\s*first[_ ]name\s*\}\}/gi;

// Replacements use callbacks so "$&" and friends in a name stay literal.
export function personalizeSubject(subject: string, firstName: string | null): string {
  const name = firstName?.trim() || 'there';
  return subject.replace(FIRST_NAME_RE, () => name);
}

export function personalize(html: string, v: PersonVars, kind: 'html' | 'text' = 'html'): string {
  const name = v.firstName?.trim() || 'there';
  // escAttr, not escHtml: the placeholder can sit inside an attribute, such as an image description.
  const filled = kind === 'html' ? escAttr(name) : name;
  let out = html.replace(FIRST_NAME_RE, () => filled);
  out = out.replace(/\{\{LINK_(\d+)\}\}/g, (_m, n: string) => (kind === 'html' ? escAttr(v.linkUrl(Number(n))) : v.linkUrl(Number(n))));
  out = out.split('{{UNSUB_URL}}').join(kind === 'html' ? escAttr(v.unsubUrl) : v.unsubUrl);
  const pixel = v.openPixelUrl ? `<img src="${escAttr(v.openPixelUrl)}" width="1" height="1" alt="" style="display:block;width:1px;height:1px;border:0;">` : '';
  out = out.replace('{{OPEN_PIXEL}}', () => pixel);
  return out;
}

/** Stable fingerprint of what will be sent; the test approval is tied to it. */
export async function contentHash(subject: string, preheader: string, html: string): Promise<string> {
  const data = new TextEncoder().encode(`${subject}\u0000${preheader}\u0000${html}`);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

export function monthLabel(d: Date, timeZone = 'America/Chicago'): string {
  return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone }).format(d);
}

export { decodeEntities };
